import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Search,
  Plus,
  RefreshCcw,
  Download,
  Save,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const n2 = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const SalaryIncrementListing = () => {
  const nav = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // your login branch

  const [showFilters, setShowFilters] = useState(true);

  // filters
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("Select");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const month = "May"; // ASP fixed

  // grid rows
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selections
  const [editIds, setEditIds] = useState([]);
  const [submitIds, setSubmitIds] = useState([]);
  const [selectAllSubmit, setSelectAllSubmit] = useState(false);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-10", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const selectBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (submitIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const ensureFinYear = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const fetchBranches = async () => {
    if (!ensureFinYear()) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/salary-increment-listing/branches`, {
        params: { finYearId: Number(finYearId) },
      });
      if (res?.data?.status) {
        const list = res.data.data || [];
        setBranches(list);

        // default to login branch if present
        if (loginBranchId) {
          // proc shape differs per DB; try match by common keys
          const found =
            list.find((x) => String(x.fld_Branchid) === String(loginBranchId)) ||
            list.find((x) => String(x.branchid) === String(loginBranchId)) ||
            null;

          if (found) {
            // ASP uses SelectedValue = branchid
            const val =
              found.fld_Branchid ?? found.branchid ?? found.value ?? loginBranchId;
            setBranch(String(val));
          }
        }
      } else {
        toast.error(res?.data?.message || "Failed to load branches");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load branches");
    }
  };

  const fetchGrid = async () => {
    if (!ensureFinYear()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-increment-listing/list`, {
        params: {
          finYearId: Number(finYearId),
          branch,
          year,
          month,
        },
      });

      if (res?.data?.status) {
        // normalize + compute totals like ASP TextChanged would do
        const data = (res.data.data || []).map((r) => {
          const grossSalary = n2(r.fld_grosssalary);
          const hikeSalary = n2(r.fld_hikesalary);
          const grossBata = n2(r.fld_grossbata);
          const hikeBata = n2(r.fld_hikebata);

          const totalSalary =
            r.fld_totalsalary != null
              ? n2(r.fld_totalsalary)
              : r.fld_TotalSalary != null
              ? n2(r.fld_TotalSalary)
              : grossSalary + hikeSalary;

          const totalBata =
            r.fld_totalbata != null ? n2(r.fld_totalbata) : grossBata + hikeBata;

          return {
            ...r,
            _grossSalary: grossSalary,
            _hikeSalary: hikeSalary,
            _totalSalary: totalSalary,
            _grossBata: grossBata,
            _hikeBata: hikeBata,
            _totalBata: totalBata,
          };
        });

        setRows(data);
        setEditIds([]);
        setSubmitIds([]);
        setSelectAllSubmit(false);
      } else {
        toast.error(res?.data?.message || "Failed to load list");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchBranches();
      // year default already set to current year (ASP)
      await fetchGrid();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNew = () => {
    // ASP: redirect to SalaryIncrementEntry.aspx
    nav("/payroll/SalaryIncrementEntry");
  };

  const handleSearch = async () => {
    await fetchGrid();
  };

  // ✅ Clear filters back to defaults and reload
  const handleClearFilters = async () => {
    setBranch("Select");
    setYear(String(new Date().getFullYear()));
    await fetchBranches();
  };

  const handleExcel = () => {
    if (!ensureFinYear()) return;
    const qs = new URLSearchParams({
      finYearId: String(Number(finYearId)),
      branch,
      year,
      month,
    });
    window.open(`${BASE_URL}/api/salary-increment-listing/export?${qs.toString()}`, "_blank");
  };

  // Hike change -> totals change (ASP TextChanged)
  const updateRowHike = (idx, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      const r = { ...copy[idx] };

      const v = value === "" ? "" : value; // keep typing
      if (field === "salary") r._hikeSalary = v === "" ? "" : n2(v);
      if (field === "bata") r._hikeBata = v === "" ? "" : n2(v);

      const grossSalary = n2(r._grossSalary);
      const grossBata = n2(r._grossBata);

      const hikeSalary = r._hikeSalary === "" ? 0 : n2(r._hikeSalary);
      const hikeBata = r._hikeBata === "" ? 0 : n2(r._hikeBata);

      r._totalSalary = grossSalary + hikeSalary;
      r._totalBata = grossBata + hikeBata;

      copy[idx] = r;
      return copy;
    });
  };

  // selections
  const toggleEdit = (key) => {
    setEditIds((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));
  };

  const toggleSubmit = (key) => {
    setSubmitIds((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));
  };

  const toggleSubmitAll = () => {
    if (selectAllSubmit) {
      setSubmitIds([]);
      setSelectAllSubmit(false);
      return;
    }
    // use incrementId as unique key for submit/edit actions
    const all = rows.map((r) => String(r.fld_incrementid));
    setSubmitIds(all);
    setSelectAllSubmit(true);
  };

  // EDIT footer action
  const saveEdits = async () => {
    if (!ensureFinYear()) return;
    if (editIds.length === 0) {
      toast.warn("Select any one..");
      return;
    }

    const items = rows
      .filter((r) => editIds.includes(String(r.fld_incrementid)))
      .map((r) => ({
        employeeId: n2(r.fld_Employeeid),
        approvalHistoryId: n2(r.fld_approvalhistoryid),
        incrementId: n2(r.fld_incrementid),
        grossSalary: n2(r._grossSalary),
        hikeSalary: n2(r._hikeSalary),
        totalSalary: n2(r._totalSalary),
        grossBata: n2(r._grossBata),
        hikeBata: n2(r._hikeBata),
        totalBata: n2(r._totalBata),
      }));

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/salary-increment-listing/edit`, {
        finYearId: Number(finYearId),
        items,
      });

      if (res?.data?.status) {
        toast.success("Successfully Updated");
        await fetchGrid();
      } else {
        toast.error(res?.data?.message || "Please retry");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // SUBMIT(Remove) footer action
  const submitRemove = async () => {
    if (!ensureFinYear()) return;
    if (submitIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    const items = rows
      .filter((r) => submitIds.includes(String(r.fld_incrementid)))
      .map((r) => ({
        employeeId: n2(r.fld_Employeeid),
        approvalHistoryId: n2(r.fld_approvalhistoryid),
        incrementId: n2(r.fld_incrementid),
        grossSalary: n2(r._grossSalary),
        grossBata: n2(r._grossBata),
      }));

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/salary-increment-listing/submit`, {
        finYearId: Number(finYearId),
        year,
        items,
      });

      if (res?.data?.status) {
        const stat = n2(res.data.stat);
        const removed = n2(res.data.removed);

        if (stat >= 1) {
          alert(`${stat} Records Payroll Generated Feature Months`);
        }

        if (removed >= 1) {
          alert("Removed Successfully..");
        } else {
          toast.info("No rows removed");
        }

        await fetchGrid();
      } else {
        toast.error(res?.data?.message || "Please retry");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // Totals (Excel adds totals row; on screen optional)
  const totals = useMemo(() => {
    const sum = (fn) => rows.reduce((a, r) => a + n2(fn(r)), 0);
    return {
      grossSalary: sum((r) => r._grossSalary),
      hikeSalary: sum((r) => r._hikeSalary),
      totalSalary: sum((r) => r._totalSalary),
      grossBata: sum((r) => r._grossBata),
      hikeBata: sum((r) => r._hikeBata),
      totalBata: sum((r) => r._totalBata),
    };
  }, [rows]);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALARY INCREMENT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {rows.length} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleAddNew} className={primaryBtn} disabled={loading}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-lg font-extrabold">Search Criteria</div>

                <div className="flex flex-wrap gap-3">
                  <button onClick={handleClearFilters} className={ghostBtn} disabled={loading}>
                    Clear
                  </button>

                  <button onClick={fetchGrid} className={ghostBtn} disabled={loading}>
                    <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </button>

                  <button onClick={handleExcel} className={ghostBtn} disabled={loading}>
                    <Download className="w-4 h-4" />
                    Excel
                  </button>

                  <button onClick={handleSearch} className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={selectBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {branches.map((b, i) => {
                      const val = String(b.fld_Branchid ?? b.branchid ?? b.value ?? "");
                      const text = String(b.fld_BranchName ?? b.branchname ?? b.text ?? val);
                      if (!val) return null;
                      return (
                        <option key={i} value={val}>
                          {text}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>
                    Date (Year) <span className="text-red-500">*</span>
                  </label>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className={selectBase + " mt-2"}>
                    {Array.from({ length: Math.max(8, new Date().getFullYear() - 2015 + 1) }).map((_, idx) => {
                      const y = String(2015 + idx);
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-bold">S.No</th>
                    <th className="px-3 py-3 text-left font-bold">Employee Name</th>
                    <th className="px-3 py-3 text-center font-bold">Employee Code</th>
                    <th className="px-3 py-3 text-left font-bold">Designation</th>
                    <th className="px-3 py-3 text-left font-bold">Location</th>

                    <th className="px-3 py-3 text-right font-bold">GrossSalary</th>
                    <th className="px-3 py-3 text-right font-bold">Hike</th>
                    <th className="px-3 py-3 text-right font-bold">TotalSalary</th>

                    <th className="px-3 py-3 text-right font-bold">GrossBata</th>
                    <th className="px-3 py-3 text-right font-bold">HikeBata</th>
                    <th className="px-3 py-3 text-right font-bold">TotalBata</th>

                    <th className="px-3 py-3 text-center font-bold">Edit</th>

                    <th className="px-3 py-3 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Remove
                        <input
                          type="checkbox"
                          checked={selectAllSubmit}
                          onChange={toggleSubmitAll}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td colSpan={13} className="px-4 py-4">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className={`px-4 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const key = String(r.fld_incrementid);

                      // ASP RowDataBound disable logic
                      const disableSalary = !(String(r.fld_hikesalary || "0") === "0" || String(r.fld_hikesalary || "") === "");
                      const disableBata =
                        !(String(r.fld_hikebata || "0") === "0" || String(r.fld_hikebata || "0.00") === "0.00");

                      return (
                        <tr
                          key={key}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>

                          <td className={`px-3 py-3 text-left text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            {r.fld_Employee}
                          </td>

                          <td className={`px-3 py-3 text-center text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {r.fld_Employeecode}
                          </td>

                          <td className={`px-3 py-3 text-left text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_designationname}
                          </td>

                          <td className={`px-3 py-3 text-left text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_BranchName}
                          </td>

                          <td className={`px-3 py-3 text-right text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {n2(r._grossSalary).toFixed(2)}
                          </td>

                          <td className="px-3 py-3 text-right">
                            <input
                              disabled={disableSalary}
                              value={r._hikeSalary === "" ? "" : String(r._hikeSalary)}
                              onChange={(e) => updateRowHike(idx, "salary", e.target.value)}
                              className={`w-24 px-2 py-2 rounded-xl border outline-none text-right ${
                                isDark ? "bg-gray-800/40 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                              } ${disableSalary ? "opacity-60 cursor-not-allowed" : ""}`}
                            />
                          </td>

                          <td className={`px-3 py-3 text-right text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                            {n2(r._totalSalary).toFixed(2)}
                          </td>

                          <td className={`px-3 py-3 text-right text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {n2(r._grossBata).toFixed(2)}
                          </td>

                          <td className="px-3 py-3 text-right">
                            <input
                              disabled={disableBata}
                              value={r._hikeBata === "" ? "" : String(r._hikeBata)}
                              onChange={(e) => updateRowHike(idx, "bata", e.target.value)}
                              className={`w-24 px-2 py-2 rounded-xl border outline-none text-right ${
                                isDark ? "bg-gray-800/40 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                              } ${disableBata ? "opacity-60 cursor-not-allowed" : ""}`}
                            />
                          </td>

                          <td className={`px-3 py-3 text-right text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                            {n2(r._totalBata).toFixed(2)}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={editIds.includes(key)}
                              onChange={() => toggleEdit(key)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={submitIds.includes(key)}
                              onChange={() => toggleSubmit(key)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                {/* Footer totals + actions */}
                {rows.length > 0 && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td colSpan={11} className="px-3 py-3 text-right text-sm font-bold">
                        Total :
                        <span className="ml-3">GrossSalary {totals.grossSalary.toFixed(2)}</span>
                        <span className="ml-3">HikeSalary {totals.hikeSalary.toFixed(2)}</span>
                        <span className="ml-3">TotalSalary {totals.totalSalary.toFixed(2)}</span>
                        <span className="ml-3">GrossBata {totals.grossBata.toFixed(2)}</span>
                        <span className="ml-3">HikeBata {totals.hikeBata.toFixed(2)}</span>
                        <span className="ml-3">TotalBata {totals.totalBata.toFixed(2)}</span>
                      </td>

                      <td className="px-3 py-3 text-center">
                        <button onClick={saveEdits} className={primaryBtn} disabled={loading}>
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                      </td>

                      <td className="px-3 py-3 text-center">
                        <button onClick={submitRemove} className={dangerBtn} disabled={loading || submitIds.length === 0}>
                          🗑 Remove
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SalaryIncrementListing;